// src/components/predictive/SHAPBarChart.tsx

import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { formatFeatureLabel } from "../../utils/formatFeatureLabel";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";

interface SHAPBarChartProps {
  topFeatures: Array<{ feature: string; shap_value: number }> | any;
  height?: number;
  showLegend?: boolean;
}

const SHAPBarChart: React.FC<SHAPBarChartProps> = ({ topFeatures, height = 220, showLegend = true }) => {
  const scaledFeatures = useMemo(() => {
    if (!Array.isArray(topFeatures)) return [];

    return topFeatures
      .map((f: any) => {
        const labelRaw = String(f?.label ?? "").trim();
        const feature = labelRaw
          ? labelRaw
          : formatFeatureLabel(String(f?.feature ?? ""));
        const raw = Number(f?.shap_value);

        // Keep the sign to show direction (positive = increases risk, negative = reduces risk)
        const shapScaled = Number.isFinite(raw) ? raw * 1000 : 0;

        return {
          feature,
          shap_value: shapScaled,
          isPositive: shapScaled >= 0
        };
      })
      .filter((d) => d.feature.length > 0 && d.shap_value !== 0)
      .sort((a, b) => Math.abs(b.shap_value) - Math.abs(a.shap_value));
  }, [topFeatures]);

  if (scaledFeatures.length === 0) {
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          No SHAP drivers available for this shipment.
        </Typography>
      </Box>
    );
  }

  const maxLabelLen = Math.max(...scaledFeatures.map((d) => d.feature.length));
  const leftMargin = Math.min(140, Math.max(100, maxLabelLen * 6));

  const minValue = Math.min(...scaledFeatures.map((d) => d.shap_value));
  const maxValue = Math.max(...scaledFeatures.map((d) => d.shap_value));
  const padPositive = maxValue > 0 ? Math.ceil(maxValue * 0.1) : 0;
  const padNegative = minValue < 0 ? Math.ceil(Math.abs(minValue) * 0.1) : 0;
  const domainMin = minValue >= 0 ? 0 : Math.floor(minValue) - padNegative;
  const domainMax = maxValue <= 0 ? 0 : Math.ceil(maxValue) + padPositive;

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Box sx={{ width: "100%", height, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={scaledFeatures}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 5, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis
              type="number"
              domain={[domainMin, domainMax]}
              tickFormatter={(value) => value.toFixed(1)}
            />
            <YAxis
              dataKey="feature"
              type="category"
              width={leftMargin - 10}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              formatter={(value: number) => [value.toFixed(2), "SHAP Value"]}
            />
            <ReferenceLine x={0} stroke="#666" strokeWidth={1} />
            <Bar dataKey="shap_value" maxBarSize={25}>
              {scaledFeatures.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.isPositive ? "#dc2626" : "#10b981"}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {showLegend && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 3, mt: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: "#dc2626", borderRadius: 1 }} />
            <Typography variant="caption">Increases risk</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: "#10b981", borderRadius: 1 }} />
            <Typography variant="caption">Reduces risk</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SHAPBarChart;
